import React, { Dispatch, SetStateAction, useMemo } from 'react';
import Link from 'next/link';
import NextImage from 'next/image';
import { Grid, Theme, Typography, useMediaQuery } from '@mui/material';
import { FiHeadphones } from '@react-icons/all-files/fi/FiHeadphones';
import { FiPlayCircle } from '@react-icons/all-files/fi/FiPlayCircle';

import { formatDate } from '@mais-retorno-front/helpers/date';
import { IContent } from '@mais-retorno-front/models/content';
import { removeHtml } from '@mais-retorno-front/helpers/format';

import {
  Container,
  ButtonContentFavorite,
  DivContentCardFooter,
  DivContent,
} from './styles';

interface ICustomizedContentCardProps {
  content: IContent;
  index: number;
  bookmarkContentExist: boolean;
  setBookmarksIds: Dispatch<SetStateAction<string[]>>;
  handleAddBookmarkContent: (content: IContent) => Promise<void>;
  handleRemoveBookmarkContent: (content_id: number) => Promise<void>;
}

export const CustomizedContentCard = ({
  content,
  index,
  setBookmarksIds,
  bookmarkContentExist,
  handleAddBookmarkContent,
  handleRemoveBookmarkContent,
}: ICustomizedContentCardProps) => {
  const showImage = useMemo(() => !!content.imageUrl, [content.imageUrl]);

  const isXsDown = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('lg')
  );

  return (
    <Container isXsDown={isXsDown} key={content?.slug}>
      <Grid
        container
        title={content?.title}
        aria-label={content?.title}
        style={{
          width: '100%',
          padding: 16,
        }}
      >
        {showImage && (
          <Grid
            item
            component="figure"
            xs={12}
            sm={4}
            style={{
              padding: 0,
              margin: 0,
              borderRadius: '5px',
              alignSelf: 'center',
              textDecoration: 'none',
            }}
          >
            <NextImage
              src={content?.imageUrl}
              width={16}
              height={9}
              alt={content?.imageAltText}
              title={content?.imageTitle}
              sizes="100vw"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                maxHeight: '230px',
              }}
              priority={index < 3}
            />
          </Grid>
        )}

        <Grid
          item
          component="div"
          xs={12}
          sm={showImage ? 7 : 11}
          sx={{
            pl: {
              xs: 0,
              sm: 2,
            },
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            textDecoration: 'none',
          }}
        >
          <DivContent>
            {content?.chapeu && (
              <Typography
                component="p"
                dangerouslySetInnerHTML={{ __html: content?.chapeu }}
              />
            )}
            <Link
              href={content.link}
              passHref
              legacyBehavior
              prefetch={false}
              target="_blank"
              rel="noopener"
            >
              <Typography
                component="h3"
                dangerouslySetInnerHTML={{ __html: content?.title }}
                style={{ fontSize: 20, marginTop: isXsDown ? 10 : 0 }}
              />
            </Link>
            <Typography
              component="p"
              dangerouslySetInnerHTML={{
                __html: removeHtml(content?.excerpt),
              }}
            />
          </DivContent>

          <DivContentCardFooter
            style={{
              flexDirection: isXsDown ? 'column' : 'row',
              alignItems: isXsDown ? 'flex-start' : 'center',
            }}
          >
            <Typography
              component="span"
              style={{
                fontWeight: 700,
                marginBottom:
                  isXsDown &&
                  (content.type === 'video' || content.type === 'podcast')
                    ? 10
                    : 0,
              }}
            >
              {formatDate(content?.date)}{' '}
              {content.chapeu &&
                content?.seo?.readingTime &&
                `· ${content?.seo?.readingTime} min de leitura`}
            </Typography>

            {content.type !== 'term' && content.type !== 'post' && (
              <Link
                href={content?.link}
                passHref
                prefetch={false}
                target="_blank"
                rel="noopener"
                style={{ width: isXsDown && '100%' }}
              >
                {content.type === 'video' ? (
                  <>
                    Assistir
                    <FiPlayCircle color="primary" size={15} />
                  </>
                ) : (
                  <>
                    Ouvir
                    <FiHeadphones color="primary" size={15} />
                  </>
                )}
              </Link>
            )}
          </DivContentCardFooter>
        </Grid>
      </Grid>
    </Container>
  );
};
